import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatMarkdown from '../../components/ChatMarkdown';
import apiClient from '../../utils/apiClient';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Plus, Send, Bot, User as UserIcon, Trash2, Edit2, Paperclip, Image as ImageIcon, FileText, X, Zap, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '../../components/ui/skeleton';
import EmptyState from '../../components/EmptyState';
import SkillFeedback from '../../components/SkillFeedback';
import StarRating from '../../components/StarRating';
import ActionApprovalCard from '../../components/ActionApprovalCard';

export default function AIAssistant() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [selectedThreadForRename, setSelectedThreadForRename] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [installedSkills, setInstalledSkills] = useState([]);
  const [selectedSkillId, setSelectedSkillId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  
  // Feedback state tracking for messages
  const [messageFeedback, setMessageFeedback] = useState({});
  const [feedbackComments, setFeedbackComments] = useState({});
  const [showCommentBox, setShowCommentBox] = useState({});
  
  // Training data dialog state - shown BEFORE upload
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // File waiting for user decision
  const [trainingType, setTrainingType] = useState('knowledge');
  
  // Temporary attachment (for skip training - not saved to DB)
  const [temporaryAttachment, setTemporaryAttachment] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchThreads();
    fetchInstalledSkills();
  }, []);

  useEffect(() => {
    if (threadId) {
      fetchMessages(threadId);
      // Keep skill selection when switching within same session
      // Don't reset here - let user's choice persist
    }
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThreads = async () => {
    try {
      setThreadsLoading(true);
      const response = await apiClient.get('/ai/threads?scope=PERSONAL');
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setThreadsLoading(false);
    }
  };

  const fetchInstalledSkills = async () => {
    try {
      const response = await apiClient.get('/skills/my-skills?filter=installed');
      setInstalledSkills(response.data);
    } catch (error) {
      console.error('Failed to fetch installed skills:', error);
      // Silently fail - skill selector is optional
    }
  };

  const fetchMessages = async (tid) => {
    try {
      setMessagesLoading(true);
      const response = await apiClient.get(`/ai/threads/${tid}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setMessagesLoading(false);
    }
  };

  const createNewThread = async () => {
    try {
      const response = await apiClient.post('/ai/threads', {
        scope: 'PERSONAL'
      });
      const newThread = response.data;
      setThreads([newThread, ...threads]);
      
      // Keep current skill selection for new conversation
      // User can change it if needed
      
      navigate(`/personal/assistant/threads/${newThread.id}`);
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Store file and show training dialog FIRST
    setPendingFile(file);
    setTrainingDialogOpen(true);
  };
  
  const handleUploadWithTrainingChoice = async (choice) => {
    // choice: 'style', 'knowledge', or 'skip'
    if (!pendingFile) return;
    
    try {
      // OPTION A: If skip, don't upload - just attach to current message
      if (choice === 'skip') {
        // Read file content directly
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          const content = e.target.result;
          
          // Store file info temporarily for this message only
          setTemporaryAttachment({
            name: pendingFile.name,
            type: pendingFile.type,
            size: pendingFile.size,
            content: content
          });
          
          setSelectedFile(pendingFile);
          toast.success(t('chat.fileAttached') || 'File attached to message (not saved to training)');
          setTrainingDialogOpen(false);
          setPendingFile(null);
        };
        
        reader.onerror = () => {
          toast.error(t('chat.fileReadFailed') || 'Failed to read file');
          setPendingFile(null);
        };
        
        // Read as text for most file types
        if (pendingFile.type.startsWith('text/') || 
            pendingFile.name.endsWith('.txt') || 
            pendingFile.name.endsWith('.md') ||
            pendingFile.name.endsWith('.json')) {
          reader.readAsText(pendingFile);
        } else if (pendingFile.type === 'application/pdf') {
          // For PDF, read as array buffer (will be processed when sending)
          reader.readAsArrayBuffer(pendingFile);
        } else {
          // For other types, read as data URL
          reader.readAsDataURL(pendingFile);
        }
        
        return;
      }
      
      // For style/knowledge: Upload to training data (existing behavior)
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('title', pendingFile.name);
      formData.append('description', `Training data (${choice}) from AI Assistant`);
      formData.append('type', 'training_text');
      
      // Capitalize first letter for consistency with Training Center
      const intent = choice.charAt(0).toUpperCase() + choice.slice(1); // 'Style' or 'Knowledge'
      formData.append('intent', intent);
      formData.append('tags', choice);
      formData.append('allowForAI', 'true');

      const response = await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadedFileId(response.data.id);
      setSelectedFile(pendingFile);
      toast.success(`Added to ${choice === 'style' ? 'Style' : 'Knowledge'} training data!`);
      
      setTrainingDialogOpen(false);
      setPendingFile(null);
      
    } catch (error) {
      toast.error(t('errors.uploadFailed'));
      setPendingFile(null);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadedFileId(null);
    setTemporaryAttachment(null); // Clear temporary attachment too
    
    // Reset the file input so the same file can be selected again
    const fileInput = document.getElementById('file-attach');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleStarClick = (messageId, rating) => {
    // Update rating
    setMessageFeedback(prev => ({
      ...prev,
      [messageId]: rating
    }));
    
    // Show comment box after clicking a star
    setShowCommentBox(prev => ({
      ...prev,
      [messageId]: true
    }));
  };

  const submitFeedbackWithComment = async (messageId) => {
    const rating = messageFeedback[messageId];
    if (!rating) {
      toast.error(t('chat.selectRating') || 'Please select a rating first');
      return;
    }
    
    await handleMessageFeedback(messageId, rating, feedbackComments[messageId] || null);
  };

  const sendMessage = async () => {
    if (!input.trim() || !threadId) return;

    setLoading(true);
    const userMessage = input;
    const attachedFileId = uploadedFileId;
    const tempAttachment = temporaryAttachment;
    setInput('');
    const tempSelectedFile = selectedFile;
    clearFile();

    try {
      // Prepare message payload
      const messagePayload = {
        content: userMessage,
        selectedSkillId: selectedSkillId,
        attachedFileId: attachedFileId  // Send the file ID if uploaded to DB
      };
      
      // If there's a temporary attachment (skip training), include its content
      if (tempAttachment) {
        let attachmentContent = '';
        
        if (typeof tempAttachment.content === 'string') {
          // Text content
          attachmentContent = `\n\n=== ATTACHED FILE: ${tempAttachment.name} ===\n${tempAttachment.content}`;
        } else if (tempAttachment.type === 'application/pdf') {
          // PDF - would need PDF.js to parse on frontend, for now just mention it
          attachmentContent = `\n\n=== ATTACHED FILE: ${tempAttachment.name} (PDF) ===\n[PDF file attached - ${tempAttachment.size} bytes]\nPlease provide key details from the document in your question.`;
        } else {
          // Other file types
          attachmentContent = `\n\n=== ATTACHED FILE: ${tempAttachment.name} ===\n[File type: ${tempAttachment.type}]\nPlease describe what you need from this file.`;
        }
        
        // Append attachment content to message
        messagePayload.content = userMessage + attachmentContent;
      }
      
      await apiClient.post(`/ai/threads/${threadId}/messages`, messagePayload);
      await fetchMessages(threadId);
      await fetchThreads();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
      setInput(userMessage);
      setSelectedFile(tempSelectedFile);
      setUploadedFileId(attachedFileId);
      setTemporaryAttachment(tempAttachment);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteThread = async (tid) => {
    setThreadToDelete(tid);
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteThread = async () => {
    try {
      await apiClient.delete(`/ai/threads/${threadToDelete}`);
      toast.success(t('success.deleted'));
      setConfirmDeleteOpen(false);
      if (threadId === threadToDelete) {
        navigate('/personal/assistant');
      }
      fetchThreads();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };


  const handleMessageFeedback = async (messageId, rating, comment = null) => {
    try {
      // Determine feedback type based on rating
      const feedbackType = rating >= 4 ? 'approved' : 'rejected';
      const feedbackTags = rating <= 2 ? ['needs_improvement'] : [];
      
      // Use provided comment or get from state
      const feedbackComment = comment !== null ? comment : (feedbackComments[messageId] || null);
      
      const response = await apiClient.post(`/ai/messages/${messageId}/feedback`, {
        feedbackType,
        rating,
        feedbackTags,
        editedContent: null,
        comment: feedbackComment
      });
      
      // Update local state to track feedback
      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: rating
      }));
      
      // Clear comment box after submission
      setFeedbackComments(prev => ({
        ...prev,
        [messageId]: ''
      }));
      setShowCommentBox(prev => ({
        ...prev,
        [messageId]: false
      }));
      
      if (response.data.memoryUpdated) {
        const learnedCount = response.data.learnedRulesCount || 0;
        if (learnedCount > 0) {
          toast.success(`Feedback recorded! Skill has learned ${learnedCount} rule${learnedCount > 1 ? 's' : ''}.`);
        } else {
          toast.success(t('chat.feedbackRecorded') || 'Feedback recorded! This will help improve the skill.');
        }
      } else {
        toast.info('Feedback noted.');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };


  const openRenameDialog = (thread) => {
    setSelectedThreadForRename(thread);
    setNewThreadTitle(thread.title);
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    try {
      await apiClient.put(`/ai/threads/${selectedThreadForRename.id}`, {
        title: newThreadTitle
      });
      toast.success(t('success.updated'));
      setRenameDialogOpen(false);
      fetchThreads();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  return (
    <div className="h-screen flex flex-col lg:flex-row" data-testid="ai-assistant-page">
      {/* Conversations Sidebar - Hidden on mobile, visible on desktop */}
      <div className="hidden lg:flex w-64 border-r border-gray-200 bg-white flex-col">
        <div className="p-4 border-b border-gray-200">
          <Button
            data-testid="new-thread-button"
            onClick={createNewThread}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('assistant.newConversation')}
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-2">
          {threadsLoading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-3 rounded-lg space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ))}
            </>
          ) : threads.map((thread) => (
            <div
              key={thread.id}
              className={`group relative p-3 rounded-lg transition-all ${
                threadId === thread.id
                  ? 'bg-primary/10 border border-primary/20'
                  : 'hover:bg-gray-100 border border-transparent'
              }`}
            >
              <button
                onClick={() => navigate(`/personal/assistant/threads/${thread.id}`)}
                className="w-full text-left"
                data-testid={`thread-${thread.id}`}
              >
                <p className="text-sm font-medium text-gray-900 truncate pr-12">{thread.title}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(thread.updatedAt).toLocaleDateString()}
                </p>
              </button>
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openRenameDialog(thread);
                  }}
                  className="p-1.5 hover:bg-gray-200 rounded"
                >
                  <Edit2 className="w-3.5 h-3.5 text-gray-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteThread(thread.id);
                  }}
                  className="p-1.5 hover:bg-gray-200 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5 text-gray-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-white min-h-0">
        {threadId ? (
          <>
            <div className="flex-1 overflow-auto">
              {messagesLoading ? (
                <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={`flex gap-4 ${i % 2 !== 0 ? '' : 'bg-gray-50 -mx-6 px-6 py-5 rounded-xl'}`}>
                      <Skeleton className="w-7 h-7 rounded-full flex-shrink-0 mt-1" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="max-w-3xl mx-auto px-6 py-4">
                  {messages.map((message, index) => (
                    <div key={message.id || index}>                      {message.actor === 'user' ? (
                        /* ─── User Message ─── */
                        <div className="flex gap-4 py-6" data-testid={`message-${message.actor}`}>
                          <div className="flex-1 flex justify-end">
                            <div className="max-w-xl rounded-2xl rounded-tr-sm px-5 py-3.5" style={{ background: '#F0F4FF' }}>
                              <p className="whitespace-pre-wrap" style={{ fontSize: '15px', lineHeight: '1.6', color: '#1F1F1F' }}>
                                {message.content}
                              </p>
                            </div>
                          </div>
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-1 text-white text-xs font-bold shadow-sm">
                            U
                          </div>
                        </div>
                      ) : (
                        /* ─── Assistant Message ─── */
                        <div className="flex gap-4 py-6" data-testid={`message-${message.actor}`}>
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {message.metadata?.skillUsed && (
                              <div className="flex items-center gap-1.5 mb-3">
                                <Zap className="w-3 h-3 text-purple-500" />
                                <span className="text-xs text-purple-600 font-medium">
                                  {message.metadata.skillUsed}
                                </span>
                              </div>
                            )}
                            <ChatMarkdown content={message.content} />
                          </div>
                        </div>
                      )}

                      {/* Feedback star rating for assistant messages */}
                      {message.actor === 'assistant' && message.metadata?.skillId && (
                        <div className="ml-11 pb-3">
                      {messageFeedback[message.id] && !showCommentBox[message.id] ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">Your rating:</span>
                          <StarRating
                            value={messageFeedback[message.id]}
                            onChange={(rating) => handleStarClick(message.id, rating)}
                            size={16}
                          />
                          <span className="text-xs text-green-600 ml-1">✓ Feedback submitted</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Rate this response:</span>
                            <StarRating
                              value={messageFeedback[message.id] || 0}
                              onChange={(rating) => handleStarClick(message.id, rating)}
                              size={16}
                            />
                          </div>
                          
                          {/* Comment box - shown after star click */}
                          {showCommentBox[message.id] && (
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Add optional feedback (e.g., 'Too formal', 'Needs more examples', 'Perfect!')"
                                value={feedbackComments[message.id] || ''}
                                onChange={(e) => setFeedbackComments(prev => ({
                                  ...prev,
                                  [message.id]: e.target.value
                                }))}
                                className="text-xs min-h-[60px] max-w-md"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => submitFeedbackWithComment(message.id)}
                                  className="h-7 px-3 text-xs"
                                >
                                  Submit Feedback
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setShowCommentBox(prev => ({
                                    ...prev,
                                    [message.id]: false
                                  }))}
                                  className="h-7 px-3 text-xs"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Approval Card - shown when skill proposes actions */}
                  {message.actor === 'assistant' && message.metadata?.actionEnvelope && (
                    <div className="ml-11">
                      <ActionApprovalCard
                        actionEnvelope={message.metadata.actionEnvelope}
                        messageId={message.id}
                        threadId={threadId}
                        onActionProcessed={(actionId, status, result) => {
                          setMessages(prev => prev.map(m => {
                            if (m.id === message.id) {
                              const envelope = { ...m.metadata.actionEnvelope };
                              envelope.proposedActions = envelope.proposedActions.map(a => {
                                if (a.actionId === actionId) {
                                  return { ...a, status };
                                }
                                return a;
                              });
                              return {
                                ...m,
                                metadata: { ...m.metadata, actionEnvelope: envelope }
                              };
                            }
                            return m;
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
                </div>
              )}
              {loading && (
                <div className="max-w-3xl mx-auto px-6">
                  <div className="flex gap-4 py-6">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
                      <Bot className="w-4 h-4 text-white animate-pulse" />
                    </div>
                    <div className="pt-2.5">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#90CAF9', animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#90CAF9', animationDelay: '160ms' }} />
                        <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#90CAF9', animationDelay: '320ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4" style={{ boxShadow: '0 -2px 8px rgba(0,0,0,0.04)' }}>
              <div className="max-w-3xl mx-auto space-y-3">
                {/* Skill Selector */}
                {installedSkills.length > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <div className="flex-1">
                      <Select value={selectedSkillId || 'auto'} onValueChange={(val) => setSelectedSkillId(val === 'auto' ? null : val)}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('assistant.selectSkill')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">🤖 {t('assistant.generalAssistant')}</SelectItem>
                          {installedSkills.map((skill) => (
                            <SelectItem key={skill.skillPackageId} value={skill.skillPackageId}>
                              {skill.name} - {skill.category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-gray-500">
                      {selectedSkillId ? t('assistant.generalAssistant') : 'AI will match your request'}
                    </p>
                  </div>
                )}
                
                {selectedFile && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    {selectedFile.type.startsWith('image/') ? (
                      <ImageIcon className="w-5 h-5 text-primary" />
                    ) : (
                      <FileText className="w-5 h-5 text-primary" />
                    )}
                    <span className="text-sm text-gray-900 flex-1">{selectedFile.name}</span>
                    <Button variant="ghost" size="sm" onClick={clearFile}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                <Textarea
                  data-testid="message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={t('assistant.typeMessage')}
                  disabled={loading}
                  rows={3}
                  className="resize-none rounded-xl border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  style={{ fontSize: '15px', lineHeight: '1.5' }}
                />
                <div className="flex items-center gap-2">
                  <label htmlFor="file-attach">
                    <input
                      id="file-attach"
                      type="file"
                      accept="image/*,.pdf,.txt,.doc,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button variant="outline" size="sm" asChild type="button" className="rounded-lg">
                      <span>
                        <Paperclip className="w-4 h-4 mr-2" />
                        {t('assistant.attachFile')}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-gray-400 flex-1">{t('chat.ctrlEnterSend') || 'Ctrl+Enter to send'}</p>
                  <Button
                    data-testid="send-message-button"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="rounded-xl px-6"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {t('assistant.send')}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={MessageCircle}
              title={t('assistant.noConversations')}
              description={t('assistant.startConvDesc')}
              actionLabel={t('assistant.newConversation')}
              onAction={createNewThread}
              secondaryText="💡 Tip: Your Twin uses your training data and skills"
            />
          </div>
        )}
      </div>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('assistant.renameConversation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newThreadTitle}
              onChange={(e) => setNewThreadTitle(e.target.value)}
              placeholder={t('assistant.conversationName')}
              rows={2}
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button onClick={handleRename} className="flex-1" disabled={!newThreadTitle.trim()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('chat.deleteConversation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this conversation? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmDeleteThread} className="flex-1 bg-red-600 hover:bg-red-700">
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add to Training Data Dialog - Shows BEFORE upload */}
      <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Add to Training Data?</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                You&apos;re about to upload:
              </p>
              <p className="text-sm font-semibold text-gray-900 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
                {pendingFile?.name}
              </p>
            </div>
            
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">How would you like to use this file?</label>
              <div className="space-y-2">
                <button
                  type="button"
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    trainingType === 'style'
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  onClick={() => setTrainingType('style')}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                      trainingType === 'style' ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {trainingType === 'style' && (
                        <div className="w-3 h-3 rounded-full bg-primary"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm mb-1">Style Training</p>
                      <p className="text-xs text-gray-600">Learn my writing style and tone from this document</p>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    trainingType === 'knowledge'
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  onClick={() => setTrainingType('knowledge')}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center flex-shrink-0 ${
                      trainingType === 'knowledge' ? 'border-primary' : 'border-gray-300'
                    }`}>
                      {trainingType === 'knowledge' && (
                        <div className="w-3 h-3 rounded-full bg-primary"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm mb-1">Knowledge Training</p>
                      <p className="text-xs text-gray-600">Learn information and facts from this document</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setTrainingDialogOpen(false);
                setPendingFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                handleUploadWithTrainingChoice('skip');
              }}
            >
              Skip Training
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                handleUploadWithTrainingChoice(trainingType);
              }}
            >
              Add to Training
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
